import React, { useMemo } from 'react';
import { Box, Typography, Stack, Tooltip } from '@mui/material';

import { DASH, EVENT_TONES } from '../dashboard/dashboardTokens';

const HOUR = 60;
const DAY = 24 * HOUR;

const toMinutes = (time) => {
    const match = time ? String(time).match(/^(\d{1,2}):(\d{2})/) : null;
    return match ? Number(match[1]) * HOUR + Number(match[2]) : null;
};

const label = (minutes) => {
    const m = ((Math.round(minutes) % DAY) + DAY) % DAY;
    return `${String(Math.floor(m / HOUR)).padStart(2, '0')}:${String(m % HOUR).padStart(2, '0')}`;
};

const formatSpan = (minutes) => {
    const total = Math.max(Math.round(minutes), 0);
    const h = Math.floor(total / HOUR);
    const m = total % HOUR;
    if (h === 0) return `${m} m`;
    return m === 0 ? `${h} h` : `${h} h ${m} m`;
};

/**
 * Zo surových udalostí dňa poskladá úseky práce a prestávok.
 *
 * Smena cez polnoc má odchod „skorší" než príchod — vtedy k nemu pripočítame
 * deň, inak by úsek na osi vyšiel záporný a zmizol.
 */
const buildSegments = (events) => {
    const work = [];
    const breaks = [];
    let openFrom = null;

    events.forEach((event) => {
        const at = toMinutes(event.time);
        if (at == null) return;

        if (event.kind === 'arrival') {
            openFrom = at;
            return;
        }

        if (event.kind === 'departure' || event.kind === 'auto_end') {
            if (openFrom == null) return;
            const end = at < openFrom ? at + DAY : at;
            work.push({ from: openFrom, to: end, auto: event.kind === 'auto_end' });
            openFrom = null;
            return;
        }

        if (event.kind === 'break_start' && event.minutes != null) {
            breaks.push({ from: at, to: at + Math.max(event.minutes, 1) });
        }
    });

    return { work, breaks, openFrom };
};

/**
 * Deň ako vodorovná os. Čísla povedia koľko, os povie kedy — a keď na nej stojí
 * aj značka bežného príchodu a odchodu, je na jeden pohľad vidieť, či sa ten
 * deň vymyká, bez toho aby to niekto musel dopočítavať.
 */
const DayHourAxis = ({ day, events, avgArrivalMin, avgDepartureMin, nowMinutes, t }) => {
    const model = useMemo(() => {
        const { work, breaks, openFrom } = buildSegments(events || []);

        // Rozrobená smena beží po „teraz" — inak by deň končil v momente
        // príchodu a os by bola prázdna.
        const live = openFrom != null && nowMinutes != null && nowMinutes > openFrom
            ? { from: openFrom, to: nowMinutes, live: true }
            : null;
        const blocks = live ? [...work, live] : work;
        if (!blocks.length) return null;

        const first = Math.min(...blocks.map((b) => b.from));
        const last = Math.max(...blocks.map((b) => b.to));

        // Okno zaokrúhlime na celé hodiny s rezervou na oboch stranách a držíme
        // aspoň osem hodín — polhodinová smena by inak vyplnila celú šírku
        // a vyzerala ako celý deň.
        let start = Math.floor((first - 30) / HOUR) * HOUR;
        let end = Math.ceil((last + 30) / HOUR) * HOUR;
        const MIN_SPAN = 8 * HOUR;
        if (end - start < MIN_SPAN) {
            const grow = (MIN_SPAN - (end - start)) / 2;
            start = Math.floor((start - grow) / HOUR) * HOUR;
            end = start + MIN_SPAN;
        }
        start = Math.max(start, 0);

        const span = end - start;
        const pct = (value) => ((value - start) / span) * 100;

        const ticks = [];
        const step = span > 14 * HOUR ? 3 * HOUR : span > 9 * HOUR ? 2 * HOUR : HOUR;
        for (let m = Math.ceil(start / step) * step; m <= end; m += step) {
            ticks.push({ at: m, left: pct(m) });
        }

        const markers = [
            { key: 'arrival', at: avgArrivalMin, tone: EVENT_TONES.arrival },
            { key: 'departure', at: avgDepartureMin, tone: EVENT_TONES.departure },
        ].filter((marker) => marker.at != null && marker.at >= start && marker.at <= end);

        return { blocks, breaks, ticks, markers, pct, start, end };
    }, [events, avgArrivalMin, avgDepartureMin, nowMinutes]);

    if (!model) return null;

    return (
        <Box sx={{ mt: 0.5 }}>
            {model.markers.length > 0 && (
                <Stack direction="row" spacing={2} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box sx={{ width: 14, height: 0, borderTop: '2px dashed', borderColor: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary">
                            {t('pages.employeeProfile.day.habitMarker')}
                        </Typography>
                    </Stack>
                </Stack>
            )}

            <Box sx={{ position: 'relative', height: 52 }}>
                {/* Pásmo dňa */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: '0 0 20px 0',
                        borderRadius: `${DASH.radiusXs}px`,
                        bgcolor: 'action.hover',
                        overflow: 'hidden',
                    }}
                >
                    {model.blocks.map((block, idx) => (
                        <Tooltip
                            key={`work-${idx}`}
                            arrow
                            disableInteractive
                            title={`${label(block.from)} → ${block.live ? t('pages.employeeProfile.strip.open') : label(block.to)}  ·  ${formatSpan(block.to - block.from)}`}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: `${model.pct(block.from)}%`,
                                    width: `${model.pct(block.to) - model.pct(block.from)}%`,
                                    bgcolor: block.auto ? 'rgba(37, 99, 235, 0.34)' : 'rgba(37, 99, 235, 0.85)',
                                    borderRight: block.auto ? '2px dashed' : 'none',
                                    borderColor: EVENT_TONES.break_start,
                                }}
                            />
                        </Tooltip>
                    ))}

                    {model.breaks.map((slot, idx) => (
                        <Tooltip
                            key={`break-${idx}`}
                            arrow
                            disableInteractive
                            title={`${t('pages.employeeProfile.day.kinds.break_start')}  ·  ${label(slot.from)} → ${label(slot.to)}  ·  ${formatSpan(slot.to - slot.from)}`}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: `${model.pct(slot.from)}%`,
                                    width: `${Math.max(model.pct(slot.to) - model.pct(slot.from), 0.6)}%`,
                                    bgcolor: EVENT_TONES.break_start,
                                }}
                            />
                        </Tooltip>
                    ))}

                    {/* Značky bežného príchodu a odchodu — deň oproti zvyku */}
                    {model.markers.map((marker) => (
                        <Box
                            key={marker.key}
                            sx={{
                                position: 'absolute',
                                top: -2,
                                bottom: -2,
                                left: `${model.pct(marker.at)}%`,
                                borderLeft: '2px dashed',
                                borderColor: 'rgba(255,255,255,0.85)',
                                mixBlendMode: 'difference',
                            }}
                        />
                    ))}
                </Box>

                {/* Hodinová mierka */}
                {model.ticks.map((tick) => (
                    <Typography
                        key={tick.at}
                        variant="caption"
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: `${tick.left}%`,
                            transform: 'translateX(-50%)',
                            color: 'text.disabled',
                            ...DASH.numeric,
                        }}
                    >
                        {label(tick.at)}
                    </Typography>
                ))}
            </Box>

            {day?.autoEnded && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t('pages.employeeProfile.day.autoNote')}
                </Typography>
            )}
        </Box>
    );
};

export default DayHourAxis;
